import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Users, ShieldCheck, MessageSquare, TrendingUp } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import TeamCard from "@/components/TeamCard";
import CTABand from "@/components/CTABand";
import api from "@/lib/api";

const VALUES = [
  { icon: Users, title: "People First", text: "We treat every client with respect, empathy and care." },
  { icon: ShieldCheck, title: "Integrity", text: "We give honest, transparent and ethical advice." },
  { icon: MessageSquare, title: "Clear Communication", text: "We explain your options in plain language." },
  { icon: TrendingUp, title: "Opportunities", text: "We help individuals, families and businesses build brighter futures." },
];

export default function About() {
  const [team, setTeam] = useState([]);
  useEffect(() => {
    api.get("/public/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <Seo title="About ApexPath | UK Immigration Advisory"
        description="ApexPath is a UK-based immigration advisory practice providing clear, practical and trusted advice to individuals, families and businesses." />
      <PageHero
        crumbs={[{ label: "Home", to: "/" }, { label: "About Us" }]}
        eyebrow="About ApexPath"
        title="People · Families ·" highlight="Opportunities"
        image="/images/about-story.jpg"
        subtitle="We are a UK-based immigration advisory practice committed to providing clear, practical and trusted advice to individuals, families, students and businesses at every stage of their immigration journey." />

      {/* Story */}
      <section className="bg-white py-16">
        <div className="container-x grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <div>
            <span className="eyebrow">Our Story</span>
            <h2 className="mt-3 font-serif text-3xl font-semibold text-navy sm:text-4xl">Why ApexPath Exists</h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              ApexPath was founded with a simple mission: to make the UK immigration system more accessible,
              clearer and less stressful for individuals, families and businesses.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              We understand that immigration is about real people, real families and real opportunities. Our team
              combines professional expertise with a personal approach, ensuring that every client receives honest,
              practical and up-to-date guidance tailored to their situation.
            </p>
            <Link to="/book-consultation" className="btn-gold mt-6">Book a Consultation <ArrowRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div className="flex gap-4 rounded-xl border border-slate-200 bg-cream p-6">
              <Target className="shrink-0 text-gold" size={28} />
              <div>
                <h3 className="font-serif text-xl font-semibold text-navy">Our Mission</h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">
                  To provide clear, practical and trusted immigration advice that helps people, families and
                  businesses build brighter futures in the UK.
                </p>
              </div>
            </div>
            <div className="flex gap-4 rounded-xl border border-slate-200 bg-cream p-6">
              <Eye className="shrink-0 text-gold" size={28} />
              <div>
                <h3 className="font-serif text-xl font-semibold text-navy">Our Vision</h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">
                  To be a respected and trusted immigration advisory practice, known for professionalism,
                  integrity and excellent client care.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="bg-cream py-16">
        <div className="container-x">
          <span className="eyebrow">Meet Our Team</span>
          <h2 className="mt-3 font-serif text-3xl font-semibold text-navy sm:text-4xl">Experienced. Dedicated. Here for You.</h2>
          <p className="mt-4 max-w-2xl text-base text-slate-600">
            Our team brings a mix of professional experience, sector knowledge and a deep commitment to supporting
            our clients with integrity and care.
          </p>
          <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {team.map((m) => <TeamCard key={m.id} member={m} />)}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-white py-16">
        <div className="container-x">
          <span className="eyebrow">Our Values</span>
          <h2 className="mt-3 mb-10 font-serif text-3xl font-semibold text-navy sm:text-4xl">What Guides Us</h2>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v) => (
              <div key={v.title}>
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full border-2 border-gold text-gold">
                  <v.icon size={22} />
                </div>
                <h4 className="text-base font-semibold text-navy">{v.title}</h4>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regulatory */}
      <section className="bg-cream py-16">
        <div className="container-x">
          <div className="rounded-xl border border-navy/10 bg-white p-8">
            <span className="eyebrow">Regulatory Information</span>
            <h2 className="mt-3 font-serif text-2xl font-semibold text-navy">Our Regulatory Position</h2>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-600">
              ApexPath Ltd provides immigration advice within its authorised scope. Our Immigration Advice Authority
              (IAA) registration number and official digital verification badge will be displayed here once formal
              registration is confirmed. We do not publish success-rate claims. Complex, refused or problem cases are
              assessed and referred appropriately.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <div className="flex h-16 w-40 items-center justify-center rounded-lg border-2 border-dashed border-slate-300 text-xs text-slate-400">
                IAA badge placeholder
              </div>
              <span className="text-sm text-slate-500">Registration number: to be confirmed</span>
            </div>
          </div>
        </div>
      </section>

      <CTABand title="Let's Talk About Your Journey"
        text="Whether you are an individual, a family or a business, we are here to listen, understand your goals and guide you with clear, practical advice." />
    </>
  );
}
